/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { CircularProgress } from '@mui/material'
import { withConfig } from '@om1/platform-utils'
import * as React from 'react'
import { PropsWithChildren, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { AspenAuth0Config, aspenAuth0Config } from '../config/aspen-auth0-config'

interface Auth0PlatformProviderComponentProps {
    children: JSX.Element
    config: AspenAuth0Config
}

export const Auth0PlatformProviderComponent: React.FunctionComponent<Auth0PlatformProviderComponentProps> = ({
    children,
    config
}: {
    children: JSX.Element
    config: AspenAuth0Config
}) => {
    const history = useHistory()

    const onRedirectCallback = (appState: any) => {
        history.push(appState?.returnTo || window.location.pathname)
    }

    return (
        <Auth0Provider
            domain={config.auth0Domain}
            clientId={config.auth0ClientId}
            authorizationParams={{
                redirect_uri: window.location.origin,
                audience: `${config.auth0AudienceDomain}`,
                scope: config.auth0AspenScopes
            }}
            onRedirectCallback={onRedirectCallback}
        >
            <AuthChecker>{children}</AuthChecker>
        </Auth0Provider>
    )
}

const AuthChecker: React.FC<PropsWithChildren> = ({ children }) => {
    const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0()

    useEffect(() => {
        console.log('Auth0 state - isLoading:', isLoading, 'isAuthenticated:', isAuthenticated)
        if (error) {
            console.error('Auth0 error:', error)
        }
        if (!isLoading && !isAuthenticated) {
            console.log('User is not authenticated, redirecting to login...')
            loginWithRedirect()
        }
    }, [isLoading, isAuthenticated, loginWithRedirect, error])

    if (isLoading) {
        return (
            <div className='min-h-screen bg-gray-100 flex items-center justify-center'>
                <div className='text-center'>
                    <CircularProgress />
                </div>
            </div>
        )
    }

    if (!isAuthenticated) {
        return <></>
    }

    return <>{children}</>
}

export const useAspenAuth = () => {
    const { isAuthenticated, isLoading, error, user, getAccessTokenSilently, logout } = useAuth0()
    return { isAuthenticated, isLoading, error, user, getAccessTokenSilently, logout }
}

export const Auth0AspenProvider = withConfig<Auth0PlatformProviderComponentProps>(Auth0PlatformProviderComponent, aspenAuth0Config)
